// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CartLineQuantityStyles } from "@insite/content-library/Widgets/Cart/CartLineQuantity";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import X from "@insite/mobius/Icons/X";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { color, ellipsis, styledScrollbar, xScreen } from "@stanion/XDL/Utilities";
import { css } from "styled-components";

interface MiniCartStyles {
    container?: InjectableCss;
    topSection?: InjectableCss;
    title?: TypographyPresentationProps;
    cartLinesWrapper?: InjectableCss;
    bottomSection?: InjectableCss;
    priceSection?: InjectableCss;
    totalItems?: TypographyProps;
    subTotalTitle?: TypographyProps;
    subTotalValue?: TypographyProps;
    actionWrapper?: InjectableCss;
    viewCartButton?: ButtonPresentationProps;
    checkoutButton?: ButtonPresentationProps;
    centerWrapper?: InjectableCss;
    notifyWrapper?: InjectableCss;
}

export const miniCartStyles: MiniCartStyles = {
    container: {
        css: css`
            height: 100%;
            > div {
                height: 100%;
            }
        `,
    },
    topSection: {
        css: css`
            background-color: ${color("common.ui8", 0.1)};
            color: ${color("text.main")};
            width: 100%;
            padding: 15px;

            & > a {
                display: inline-block;
            }
        `,
    },
    title: {
        variant: "h3",
        color: "primary.main",
        css: css`
            margin-bottom: 0;
        `,
    },

    cartLinesWrapper: {
        css: css`
            height: calc(100vh - 240px);
            margin-top: 30px;
            padding: 0 15px;
            ${styledScrollbar(6)}
        `,
    },
    bottomSection: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            padding: 20px 15px;
            padding-bottom: 40px;
            border-top: 1px solid ${color("common.ui3", 0.7)};
            position: absolute;
            bottom: 0;
            max-width: 510px;
        `,
    },
    priceSection: {
        css: css`
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            margin-bottom: 12px;
        `,
    },
    totalItems: {
        size: 16,
        weight: "normal",
        color: "text.main",
        css: css`
            letter-spacing: 0;
            line-height: 26px;
        `,
    },
    subTotalTitle: {
        size: 14,
        weight: "normal",
        color: "text.main",
        css: css`
            text-transform: capitalize;
            letter-spacing: 0;
            line-height: 22px;
            margin-right: 8px;
        `,
    },
    subTotalValue: {
        color: "text.main",
        size: 20,
        weight: "bold",
        css: css`
            letter-spacing: 0;
            line-height: 26px;
        `,
    },
    actionWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            position: relative;
            ${xScreen("lte", "xs")} {
                flex-direction: column;
            }
        `,
    },
    viewCartButton: {
        variant: "primary",
        sizeVariant: "large",
        mergeCss: true,
        css: css`
            ${xScreen("lte", "xs")} {
                width: 100%;
                margin-bottom: 10px;
            }
        `,
    },
    checkoutButton: {
        sizeVariant: "large",
        mergeCss: true,
        css: css`
            ${xScreen("lte", "xs")} {
                width: 100%;
                margin-bottom: 10px;
            }
        `,
    },
    centerWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            width: 100%;
            max-width: 470px;
            min-width: 250px;
        `,
    },
    notifyWrapper: {
        css: css`
            max-width: calc(100% - 10px);
            padding: 50px 10px;

            > span {
                width: 100%;
            }
        `,
    },
};

interface MiniCartLineStyles {
    cartLineWrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    cartLineImage?: LazyImageProps;
    detailsWrapper?: InjectableCss;
    closeWrapper?: InjectableCss;
    brandWrapper?: InjectableCss;
    brandLink?: LinkPresentationProps;
    removeCartLineLink?: LinkPresentationProps;
    lineDescription?: LinkPresentationProps;
    partNumbers?: {
        wrapper: InjectableCss;
        lable: TypographyProps;
        value: TypographyProps;
    };
    qtyAndPriceWrapper?: InjectableCss;
    qtyWrapper?: InjectableCss;
    netPrice?: TypographyProps;
    cartLineQuantityStyles?: CartLineQuantityStyles;
    errorMessagesWrapper?: InjectableCss;
}

// cart lines style
export const miniCartLineStyles: MiniCartLineStyles = {
    cartLineWrapper: {
        css: css`
            display: flex;
            position: relative;
            margin-bottom: 10px;
            padding-bottom: 16px;
            &:not(:last-child) {
                border-bottom: 1px solid ${color("common.ui3", 0.7)};
            }
        `,
    },
    imageWrapper: {
        css: css`
            height: 92px;
            width: 92px;
            overflow: hidden;
            margin-right: 12px;
            flex-shrink: 0;
        `,
    },
    cartLineImage: {
        width: "92px",
        height: "92px",
        css: css`
            max-height: 100%;
            max-width: 100%;
            img {
                height: auto;
                max-height: 100%;
                max-width: 100%;
                width: auto;
            }
        `,
    },
    detailsWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            flex: 1;
        `,
    },
    brandWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            position: relative;
        `,
    },
    brandLink: {
        typographyProps: {
            size: 12,
            weight: "normal",
            css: css`
                margin-bottom: 2px;
                letter-spacing: 0.5px;
                line-height: 14px;
                ${ellipsis(1)}
            `,
        },
        css: css`
            margin-right: 10px;
        `,
    },
    removeCartLineLink: {
        icon: {
            iconProps: {
                src: X,
                size: 16,
                color: "common.ui7",
                css: css`
                    margin-right: 3px;
                    &&&:hover {
                        color: ${color("danger.main")};
                    }
                `,
            },
        },
    },
    lineDescription: {
        typographyProps: {
            size: 12,
            weight: "bold",
            color: "text",
            css: css`
                letter-spacing: 0;
                line-height: 15px;
                ${ellipsis(2)}

                &&& {
                    color: ${color("text.main")};
                }
            `,
        },
        css: css`
            margin-bottom: 5px;
            max-width: 294px;
        `,
    },
    partNumbers: {
        wrapper: {
            css: css`
                display: flex;
                flex-direction: row;
            `,
        },
        lable: {
            size: 12,
            weight: "bold",
            color: "primary.main",
            css: css`
                margin-bottom: 10px;
                letter-spacing: 0;
                line-height: 13px;
                margin-right: 5px;
                ${ellipsis(1)};
            `,
        },
        value: {
            size: 12,
            weight: "normal",
            color: "text.main",
            css: css`
                letter-spacing: 0;
                line-height: 13px;
                margin-right: 5px;
                ${ellipsis(1)};
            `,
        },
    },
    qtyAndPriceWrapper: {
        css: css`
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            justify-content: space-between;
        `,
    },
    qtyWrapper: {
        css: css`
            margin-right: 5px;
            display: flex;
            align-items: center;
            flex-wrap: wrap;
        `,
    },
    netPrice: {
        size: 18,
        weight: "bold",
        color: "text",
        css: css`
            margin-bottom: 2px;
            ${ellipsis(1)}
            letter-spacing: 0;
            line-height: 26px;
        `,
    },

    cartLineQuantityStyles: {
        editableQuantityTextField: {
            sizeVariant: "small",
            labelProps: {
                css: css`
                    display: none;
                `,
            },
            cssOverrides: {
                formField: css`
                    width: auto;
                `,
                inputSelect: css`
                    width: 74px;
                    text-align: center;
                    font-size: 13px;
                    font-weight: 600;
                    letter-spacing: 0;
                    line-height: 18px;
                `,
            },
        },
    },
    errorMessagesWrapper: {
        css: css`
            width: 100%;
            margin-top: 10px;
            & > div:not(:last-child) {
                margin-bottom: 15px;
            }
        `,
    },
};
